import type { OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import kalendae from 'kalendae/build/kalendae';
import moment from 'moment-timezone';

import { RtInput } from '../../utils/rt-input';

const Kalendae = kalendae.bind(kalendae.prototype);
Kalendae.moment = moment;

export type DatePickerDirection = 'any' | 'past' | 'today-past' | 'today-future' | 'future';

// Value prop:
// - In "single" mode, `value` is a YYYY-MM-DD string.
// - In "multiple" mode, `value` is an array of multiple dates.
// - In "range" mode, `value` is an array of two dates.
class KalendaeType {
  public draw: () => void;
  public getSelectedRaw: () => moment.Moment[];
  public setSelected: (value: string | string[], flag: boolean) => void;
  public subscribe: (event: string, callback: (value: string) => void) => void;
  public container: HTMLElement;
  public settings: {
    dateClassMap: { [key: string]: string };
    weekStart: 0 | 1;
  };
}

// TODO make sure the focus and blur events are working correctly, or get rid of them, if we can't rely on them
@Component({
  selector: 'rt-date-picker',
  template: '',
  styleUrls: ['./date-picker.component.less'],
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.kalendae-has-no-padding]': 'hasNoPadding',
    '(focus)': 'focus.emit($event)',
    '(blur)': 'blur.emit($event)',
  },
  standalone: true,
})
export class DatePickerComponent
  extends RtInput<string | string[]>
  implements OnDestroy, OnChanges
{
  // If it's set to true then today's date will be applied as "selected"
  @Input() public selectNowByDefault: boolean = true;

  // Allow clicks on days that fall outside of the currently focused month
  @Input() public dayOutOfMonthClickable: boolean = false;

  // Dates to be disallowed from selection
  @Input() public blackout: ((date: any) => boolean) | null = null;

  // the number of calendar view
  @Input() public months: number = 1;

  // single | range | multiple
  @Input() public mode: 'single' | 'range' | 'multiple' = 'single';

  // accepted values: past, today-past, any, today-future, future
  @Input() public direction: DatePickerDirection = 'any';

  // A key/value collection of css classes organized by date
  @Input() public dateClassMap: { [key: string]: string };

  @Input() public useYearNav: boolean = true;

  @Input() public hasNoPadding: boolean = false;

  @Input() public viewStartDate: string;

  @Output() public monthChange: EventEmitter<string> = new EventEmitter();

  private datepickerInstance?: KalendaeType;
  private duringDatepickerValueChange: boolean = false;

  constructor(
    private elementRef: ElementRef,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  public ngOnInit(): void {
    this.setPickerInstance();
  }

  public ngOnDestroy(): void {
    this.destroyPickerInstance();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.datepickerInstance && changes.value) {
      if (changes.value.previousValue !== changes.value.currentValue) {
        this.setDatepickerValue(changes.value.currentValue);
      }
    }

    if (this.datepickerInstance && changes.dateClassMap) {
      if (changes.dateClassMap.previousValue !== changes.dateClassMap.currentValue) {
        this.datepickerInstance.settings.dateClassMap = changes.dateClassMap.currentValue;
        this.datepickerInstance.draw();
      }
    }

    if (this.datepickerInstance && changes.weekStartDay) {
      if (changes.weekStartDay.previousValue !== changes.weekStartDay.currentValue) {
        // Because Kalendlee sets header nodes when it's initialized
        // and it skips it in the redraw function we can't change weekStartDay dynamically
        this.destroyPickerInstance();
        this.setPickerInstance();
      }
    }
  }

  protected setValue(value: string | string[]): void {
    this.value = value;
    this.setDatepickerValue(value);
  }

  // eslint-disable-next-line max-lines-per-function
  private initDatepicker(): KalendaeType {
    let value: string | string[] | moment.Moment | moment.Moment[] | null = this.value;
    if (!value && this.selectNowByDefault) {
      value = this.mode === 'single' ? moment() : [moment()];
    } else if (Array.isArray(value)) {
      value = value.map(v => {
        if (typeof v === 'string' && v.length === 10) {
          return moment(v, 'YYYY-MM-DD');
        } else {
          // eslint-disable-next-line no-console
          (console.warn || console.log)(
            `Unrecognized format of "value" prop passed to rt-date-picker: "${v}". It should be either string like "YYYY-MM-DD" or null.`,
          );
          return moment(v);
        }
      });
    } else if (typeof value === 'string' && value.length === 10) {
      value = moment(value, 'YYYY-MM-DD');
    } else if (value) {
      // eslint-disable-next-line no-console
      (console.warn || console.log)(
        `Unrecognized format of "value" prop passed to rt-date-picker: "${value}". It should be either string like "YYYY-MM-DD" or null.`,
      );
      value = moment(value);
    } else {
      value = null;
    }

    if (value && this.mode === 'single' && Array.isArray(value)) {
      throw new Error(
        "DatePickerComponent.value has to be a date when DatePickerComponent.mode === 'single'",
      );
    }
    if (value && this.mode !== 'single' && !Array.isArray(value)) {
      throw new Error(
        "DatePickerComponent.value has to be an array when DatePickerComponent.mode !== 'single'",
      );
    }

    return new Kalendae({
      attachTo: this.elementRef.nativeElement,
      months: this.months,
      mode: this.mode,
      weekStart: moment.localeData().firstDayOfWeek(),
      direction: this.direction,
      blackout: this.blackout,
      dayOutOfMonthClickable: this.dayOutOfMonthClickable,
      selected: value,
      useYearNav: this.useYearNav,
      ...(this.dateClassMap ? { dateClassMap: this.dateClassMap } : {}),
      ...(this.viewStartDate ? { viewStartDate: this.viewStartDate } : {}),
    });
  }

  private listenToDatepickerChanges(datepickerInstance: KalendaeType): void {
    datepickerInstance.subscribe('change', () => {
      if (this.duringDatepickerValueChange) {
        return;
      }
      const selectedDates = datepickerInstance.getSelectedRaw().map(m => m.format('YYYY-MM-DD'));
      const nextValue =
        this.mode === 'single'
          ? selectedDates.length > 0
            ? selectedDates[0]
            : null
          : selectedDates;
      this.handleChange(nextValue!);
    });

    datepickerInstance.subscribe('view-changed', month => {
      this.monthChange.emit(month);
    });
  }

  private setDatepickerValue(value: string | string[]): void {
    if (this.datepickerInstance) {
      try {
        this.duringDatepickerValueChange = true;
        this.datepickerInstance.setSelected(value, true);
      } catch (e) {
        console.error(e);
      } finally {
        this.duringDatepickerValueChange = false;
      }
    }
  }

  private destroyPickerInstance(): void {
    if (this.datepickerInstance) {
      if (this.datepickerInstance.container && this.datepickerInstance.container.parentNode) {
        this.datepickerInstance.container.parentNode.removeChild(this.datepickerInstance.container);
      }
      delete this.datepickerInstance;
    }
  }

  private setPickerInstance(): void {
    this.datepickerInstance = this.initDatepicker();

    if (this.datepickerInstance) {
      this.listenToDatepickerChanges(this.datepickerInstance);
    }
  }
}
